import { type ReactNode, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import type { AppDispatch } from "~/app/store";
import { useGetMinicartQuery } from "~/features/cart/minicart/minicartApi";
import {
  invalidateMinicartProjection,
  replaceMinicartProjection,
  selectMinicartProjectionVersion,
} from "~/features/cart/minicart/minicartRedux";
import { activeCartResourceId } from "~/features/cart/cart/utils/cartResourceIdentity";
import { userRolesAreAuthenticated } from "~/domains/user/utils/roles";
import { occCartUserPathFor, type OccCartUserPath } from "~/app/api/occ/occUserPath";
import { useSessionState } from "./SessionProvider";

export function MinicartProvider({ children }: { children: ReactNode }) {
  const dispatch = useDispatch<AppDispatch>();
  const session = useSessionState();
  const authenticated = userRolesAreAuthenticated(session.data?.user.roles ?? []);
  const activeCartId = activeCartResourceId(session.data?.activeCart, authenticated);
  const userPath = occCartUserPathFor(authenticated);
  const projectionVersion = useSelector(selectMinicartProjectionVersion);

  useEffect(() => {
    if (session.cartActionsDisabled) {
      dispatch(invalidateMinicartProjection());
    } else if (session.synchronized && !activeCartId) {
      dispatch(replaceMinicartProjection(0));
    }
  }, [activeCartId, dispatch, session.cartActionsDisabled, session.synchronized]);

  return (
    <>
      {children}
      {session.synchronized && activeCartId ? (
        <MinicartProjectionLoader
          activeCartId={activeCartId}
          projectionVersion={projectionVersion}
          userPath={userPath}
        />
      ) : null}
    </>
  );
}

function MinicartProjectionLoader({
  activeCartId,
  projectionVersion,
  userPath,
}: {
  activeCartId: string;
  projectionVersion: number;
  userPath: OccCartUserPath;
}) {
  useGetMinicartQuery({ activeCartId, projectionVersion, userPath });
  return null;
}
